import React from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import About from "@/components/About";
import Services from "@/components/Services";
import Why from "@/components/Why";
import AppShowcase from "@/components/AppShowcase";
import Partner from "@/components/Partner";
import Testimonials from "@/components/Testimonials";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import AdminPortalRedirect from "@/components/AdminPortalRedirect";
import PolicyPage from "@/pages/PolicyPage";
import {
  DEFAULT_REGION,
  POLICY_ALIASES,
  POLICY_LINKS,
  REGION_ORDER,
  REGIONS,
} from "@/lib/policies";

function Home() {
  const location = useLocation();

  // Returning from a policy page via `/#services` should still land on the
  // section, so resolve the hash once the sections have mounted.
  React.useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = location.hash.slice(1);
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => clearTimeout(t);
  }, [location]);

  return (
    <>
      <Navbar />
      <main>
        <Hero />
        <About />
        <Services />
        <Why />
        <AppShowcase />
        <Partner />
        <Testimonials />
        <Contact />
      </main>
      <Footer />
      <WhatsAppFab />
    </>
  );
}

/**
 * Short top-level URLs for each legal document, e.g. `/privacy-policy` and
 * `/ae/data-deletion`. These are the links handed to Play Console, App Store
 * Connect and the payment gateways, so they must keep resolving — they redirect
 * to the canonical `/policy/<slug>` (or `/ae/policy/<slug>`) page.
 */
const SHORT_POLICY_PATHS = [
  ...POLICY_LINKS.map((p) => [p.slug, p.slug]),
  ...Object.entries(POLICY_ALIASES),
];

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin/*" element={<AdminPortalRedirect />} />

          {REGION_ORDER.map((key) => {
            const { base } = REGIONS[key];
            return (
              <React.Fragment key={key}>
                <Route
                  path={`${base}/policy/:slug`}
                  element={<PolicyPage region={key} />}
                />
                <Route
                  path={`${base}/policy`}
                  element={<Navigate to={`${base}/policy/privacy-policy`} replace />}
                />
                {SHORT_POLICY_PATHS.map(([path, slug]) => (
                  <Route
                    key={`${key}-${path}`}
                    path={`${base}/${path}`}
                    element={<Navigate to={`${base}/policy/${slug}`} replace />}
                  />
                ))}
                {/* A region prefix only scopes the legal pages; there is one
                    landing page, so `/ae` and `/np` fall back to it. */}
                {key !== DEFAULT_REGION && (
                  <Route path={base} element={<Navigate to="/" replace />} />
                )}
              </React.Fragment>
            );
          })}
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}
